import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Phone,
  FileDown,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { currency, formatDate } from "../lib/utils-app";
import { downloadOverduePaymentsPDF } from "../lib/pdf";

/**
 * Red "Outstanding Overdue Payments" banner — visible to all users.
 * Used on both the Dashboard and the Orders page so it's impossible to
 * miss regardless of where staff land first. Click to expand the full
 * list with per-customer WhatsApp reminder buttons.
 */
export default function OutstandingAlert({ title = "overdue payments" }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api
      .get("/reports/orders-outstanding?min_days=7")
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);

  if (!data) return null;

  // Zero-state: hide the banner entirely when nothing is overdue.
  // Users asked not to see a persistent green bar when there's no
  // action to take; the banner is for action, not reassurance.
  if (data.count === 0) return null;

  const whatsappReminder = (o) => {
    const msg = encodeURIComponent(
      `Dear ${o.customer_name || "Customer"}, this is a gentle reminder from Adams regarding the pending balance of ₹${Number(
        o.balance_due
      ).toLocaleString("en-IN")} for the event on ${formatDate(
        o.event_date || o.delivery_date
      )} (Order #${o.order_number || ""}). Please arrange the payment at the earliest. — Adams`
    );
    const phone = (o.customer_phone || "").replace(/\D/g, "").slice(-10);
    if (!phone) {
      toast.error("No phone on file for this customer");
      return;
    }
    window.open(`https://wa.me/91${phone}?text=${msg}`, "_blank");
  };

  const callCustomer = (o) => {
    const phone = (o.customer_phone || "").replace(/\D/g, "").slice(-10);
    if (!phone) {
      toast.error("No phone on file for this customer");
      return;
    }
    window.location.href = `tel:+91${phone}`;
  };

  return (
    <div
      className="border-2 border-red-200 bg-red-50 rounded-xl overflow-hidden"
      data-testid="outstanding-alert"
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 p-4 hover:bg-red-100 transition-colors"
        data-testid="outstanding-toggle"
      >
        <div className="h-10 w-10 rounded-lg bg-red-600 text-white flex items-center justify-center shrink-0 animate-pulse">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="font-heading font-bold text-red-900 flex items-center gap-2 flex-wrap">
            {data.count} {title}
            <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px]">
              {data.min_days}+ days
            </Badge>
          </div>
          <div className="text-sm text-red-800 mt-0.5">
            Total outstanding:{" "}
            <span className="font-bold">{currency(data.total_outstanding)}</span>{" "}
            — tap to view & remind via WhatsApp
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-red-700" />
        ) : (
          <ChevronDown className="h-5 w-5 text-red-700" />
        )}
      </button>
      {expanded && (
        <div className="border-t border-red-200 bg-white">
          {/* Prominent Download PDF button strip */}
          <div className="px-3 sm:px-4 py-3 bg-red-50/50 border-b border-red-100 flex justify-end">
            <Button
              size="sm"
              onClick={() =>
                downloadOverduePaymentsPDF(data.orders, {
                  min_days: data.min_days,
                })
              }
              data-testid="overdue-download-pdf"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold shadow-lg shadow-red-600/30 hover:shadow-red-700/40 h-10 px-5 ring-2 ring-red-200 hover:ring-red-300 transition-all"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Download PDF report
            </Button>
          </div>
          <div className="divide-y divide-red-100">
          {data.orders.map((o) => (
            <div
              key={o.id}
              className="p-3 sm:p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3"
              data-testid="outstanding-row"
            >
              <div className="flex-1 min-w-0 sm:min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-slate-700">
                    #{o.order_number}
                  </span>
                  <span className="font-semibold text-slate-950">
                    {o.customer_name || "—"}
                  </span>
                  <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px]">
                    {o.overdue_days}d
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Event {formatDate(o.event_date || o.delivery_date)} ·{" "}
                  {o.customer_phone || "no phone"}
                </div>
              </div>
              <div
                className="flex items-center justify-between gap-2 sm:gap-3 w-full sm:w-auto"
                data-testid="outstanding-row-actions"
              >
                <div
                  className="font-heading font-bold text-red-700 tabular-nums text-base sm:text-sm whitespace-nowrap"
                  data-testid="outstanding-row-amount"
                >
                  {currency(o.balance_due)}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => callCustomer(o)}
                    data-testid="outstanding-call"
                    className="bg-slate-900 hover:bg-slate-800 text-white h-9 px-3"
                    title="Call customer"
                  >
                    <Phone className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Call</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => whatsappReminder(o)}
                    data-testid="outstanding-whatsapp"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3"
                  >
                    <MessageCircle className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
